import React, { useEffect } from 'react'
import { useTranslation, Trans } from 'next-i18next'
import { UseFormReturn } from 'react-hook-form'
import styled from '@emotion/styled'
import CheckboxField from 'modules/ui/molecules/FormComponents/CheckboxField'
import Row from 'modules/ui/atoms/Layout/Row'
import DonationOptionFormWrapper from './donation-option-form-wrapper'
import { object, boolean } from 'yup'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { DEFAULT_THEME_COLORS } from 'tokens/theme'
import { GFM_PRO_TERMS_URL } from 'utils/constants/misc'

const Wrapper = styled.div({
  a: {
    textDecoration: 'underline',
    color: DEFAULT_THEME_COLORS['--theme-color__text-general'],
  },
  '.checkbox-container': {
    alignItems: 'flex-start',
  },
})

export type OptInFormData = {
  optIn: boolean
}

export type OptInSmsFormData = {
  optInSms: boolean
}

const SmsOptInContent = () => {
  const config = useConfig()
  const { i18n } = useTranslation()
  const language = i18n.language

  if (!config.organization.name) {
    return null
  }

  return (
    <span>
      <Trans
        i18nKey="orderOptInFields.smsOptIn.description"
        values={{ orgName: config.organization.name }}
        components={{
          terms: (
            <a
              href={
                GFM_PRO_TERMS_URL[language as keyof typeof GFM_PRO_TERMS_URL] ||
                GFM_PRO_TERMS_URL.default
              }
              onClick={(e) => e.stopPropagation()}
              target="_blank"
            >
              Terms
            </a>
          ),
          privacy: (
            <a
              href={config.organization?.privacy_policy_url ?? '#'}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
            >
              Privacy Policy
            </a>
          ),
        }}
        defaultValue="By checking this box you agree to receive periodic automated and nonautomated text messages, including marketing messages, from or on behalf of {{ orgName }} at the phone number above. Consent is not required. <terms>Terms</terms> and <privacy>Privacy Policy</privacy> apply. Msg and data rates may apply."
      />
    </span>
  )
}

const OptInFieldComponent = ({ form }: { form: UseFormReturn<OptInFormData> }) => {
  const config = useConfig()
  const order = useOrder()

  const {
    watch,
    formState: { errors },
  } = form

  const optIn = watch('optIn')

  useEffect(() => {
    order.contact.updateField('optIn', optIn)
  }, [optIn, order.contact])

  return (
    <Row>
      <CheckboxField
        id="optIn"
        dataId="contact-opt-in-checkbox"
        error={errors.optIn ? { message: errors.optIn.message || 'Invalid value' } : undefined}
      >
        {config.optIn.wording}
      </CheckboxField>
    </Row>
  )
}

const OptInSmsFieldComponent = ({ form }: { form: UseFormReturn<OptInSmsFormData> }) => {
  const order = useOrder()

  const {
    watch,
    formState: { errors },
  } = form

  const optInSms = watch('optInSms')

  useEffect(() => {
    order.contact.updateField('optInSms', optInSms)
  }, [optInSms, order.contact])

  return (
    <Wrapper>
      <Row>
        <CheckboxField
          id="optInSms"
          dataId="contact-opt-in-sms-checkbox"
          error={
            errors.optInSms ? { message: errors.optInSms.message || 'Invalid value' } : undefined
          }
        >
          <SmsOptInContent />
        </CheckboxField>
      </Row>
    </Wrapper>
  )
}

export const OptInFormField = () => {
  const validationSchema = object({
    optIn: boolean().required(),
  })

  const useOptInValues = (): OptInFormData => {
    const order = useOrder()

    return {
      optIn: order.contact.value.optIn,
    }
  }

  return (
    <DonationOptionFormWrapper<OptInFormData>
      formName="optIn"
      errorMessage="Please review your opt-in preference"
      validationSchema={validationSchema}
      useInitialValues={useOptInValues}
    >
      {(form) => <OptInFieldComponent form={form} />}
    </DonationOptionFormWrapper>
  )
}

export const OptInSmsFormField = () => {
  const validationSchema = object({
    optInSms: boolean().required(),
  })

  const useOptInSmsValues = (): OptInSmsFormData => {
    const order = useOrder()

    return {
      optInSms: order.contact.value.optInSms,
    }
  }

  return (
    <DonationOptionFormWrapper<OptInSmsFormData>
      formName="optInSms"
      errorMessage="Please review your SMS opt-in preference"
      validationSchema={validationSchema}
      useInitialValues={useOptInSmsValues}
    >
      {(form) => <OptInSmsFieldComponent form={form} />}
    </DonationOptionFormWrapper>
  )
}
